import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { GTDialog, GTDialogBody, GTDialogFooter, GTDialogHeading } from '@/components/ui/gt-dialog'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { DateTime } from 'luxon'
import { useModifyRecurringTask } from '../../services/api/recurring-tasks.hooks'
import { icons } from '../../styles/images'
import { RecurrenceRate } from '../../utils/enums'
import { TRecurringTaskTemplate } from '../../utils/types'
import { stopKeydownPropogation } from '../../utils/utils'
import { Icon } from '../atoms/Icon'
import {
    formatRecurrenceRateForScheduleButton,
    getRecurrenceRateSelectorOptions,
} from '../molecules/recurring-tasks/recurringTasks.utils'
import GTSelect from '../radix/GTSelect'
import { RecurringHookPopover } from './RecurringGoalHooks'
import SectionLabel from './SectionLabel'
import { TGoal, TGoalPatch, formatGoalAmount } from './goalTypes'
import {
    WEEK_DAY_LETTERS,
    blockMinutes,
    cellDescription,
    cellFillRatio,
    formatMinutes,
    hasWeekRead,
    heldMinutesPerWeek,
    isHeld,
    repeatWeekCells,
    repeatsSummary,
} from './repeatsRules'

/**
 * Repeats — the only representation of repeated contribution toward a goal.
 *
 * A chip row cannot carry cadence, which is why the advisory contributor labels and the real
 * recurring hooks used to collide in one. These are rows: cadence, a week ribbon, and the time the
 * repeat holds, all derived in repeats.ts from the recurrence rate and one stored block length.
 * Nothing here is logged by hand.
 */

const LENGTH_SHORTCUT_MINUTES = [15, 30, 45, 60, 90]
const DEFAULT_BLOCK_MINUTES = 30

const isWeekday = (day?: number): boolean => day !== undefined && day >= 1 && day <= 7

const WEEK_READ_RATES = [RecurrenceRate.DAILY, RecurrenceRate.WEEK_DAILY, RecurrenceRate.WEEKLY]

const secondsToTimeInput = (seconds: number): string =>
    DateTime.fromSeconds(0, { zone: 'utc' }).plus({ seconds }).toFormat('HH:mm')

const timeInputToSeconds = (value: string): number => {
    const [hours, minutes] = value.split(':').map((part) => parseInt(part, 10))
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return 0
    return hours * 60 * 60 + minutes * 60
}

/** Seven cells, Mon–Sun. Bar height is the block length; text always says the same thing. */
const WeekRibbon = ({ template, showDayLetters }: { template: TRecurringTaskTemplate; showDayLetters: boolean }) => (
    <div className="flex shrink-0 items-end gap-[3px]">
        {repeatWeekCells(template).map((cell, index) => (
            <div key={cell.weekday} className="flex w-[11px] flex-col items-center gap-[3px]">
                {/* Each cell names itself, so a 7px bar is never the only channel for held time. */}
                <div
                    className="flex h-5 w-full items-end justify-center"
                    role="img"
                    title={cellDescription(cell)}
                    aria-label={cellDescription(cell)}
                >
                    {cell.state === 'held' && (
                        <div
                            className="w-full rounded-[2px] bg-foreground/80"
                            style={{ height: `${Math.round(cellFillRatio(cell.minutes) * 100)}%` }}
                        />
                    )}
                    {cell.state === 'due' && (
                        <div className="h-2 w-full rounded-[2px] border border-dashed border-border" />
                    )}
                    {cell.state === 'none' && <div className="mb-[3px] size-[3px] rounded-full bg-border" />}
                </div>
                {/* Letters render once and are hidden-but-spaced after, so columns stay aligned. */}
                <span className={cn('text-label-sm text-muted-foreground/70', !showDayLetters && 'invisible')}>
                    {WEEK_DAY_LETTERS[index]}
                </span>
            </div>
        ))}
    </div>
)

const RepeatRow = ({
    template,
    showDayLetters,
    onRemove,
    onHoldTime,
}: {
    template: TRecurringTaskTemplate
    showDayLetters: boolean
    onRemove: () => void
    onHoldTime: () => void
}) => {
    const held = isHeld(template)
    const disabled = !template.is_enabled
    const cadence = formatRecurrenceRateForScheduleButton(template)
    const weekRead = hasWeekRead(template)
    const cadenceLine = disabled
        ? `${cadence} · disabled — no time held`
        : held
        ? `${cadence} · ${formatMinutes(blockMinutes(template))} each`
        : cadence

    return (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-hairline py-2 last:border-b-0">
            <Icon icon={icons.arrows_repeat} color="gray" size="small" />
            <div className="min-w-0 flex-1">
                <p className={cn('truncate text-body-sm text-foreground', disabled && 'text-muted-foreground')}>
                    {template.title}
                </p>
                <p className="truncate text-label-md text-muted-foreground">{cadenceLine}</p>
            </div>
            <div className="flex basis-full items-center gap-3 sm:basis-auto">
                {weekRead && <WeekRibbon template={template} showDayLetters={showDayLetters} />}
                <span
                    className={cn(
                        'w-16 shrink-0 text-right text-label-md tabular-nums',
                        held ? 'text-foreground' : 'text-muted-foreground'
                    )}
                >
                    {held ? formatMinutes(heldMinutesPerWeek(template)) : 'not held'}
                </span>
                <button
                    type="button"
                    onClick={onHoldTime}
                    // Several rows carry the same words, so the row's own label names which repeat.
                    aria-label={`${held ? 'Edit held time' : 'Hold time'} for ${template.title}`}
                    className="h-6 shrink-0 rounded-md px-1.5 text-label-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                    {held ? 'Edit' : 'Hold time'}
                </button>
                <button
                    type="button"
                    aria-label={`Remove ${template.title}`}
                    onClick={onRemove}
                    className="flex size-6 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                    <Icon icon={icons.x} color="gray" size="small" />
                </button>
            </div>
        </div>
    )
}

/**
 * Hold time — three fields, and the only place a block is written.
 *
 * Days are read from the recurrence rate, so there is no separate day picker that could disagree
 * with it. Saving writes the block onto the template; the backend then carries it onto every
 * occurrence it generates, and credit still arrives only when one is finished.
 *
 * It only offers the rates that have a week read. A monthly or yearly template also needs the day
 * of the month (and the month) the recurrence lands on, and a template left missing those fails
 * getValidCreationTimeNearLastBackfillAttempt — which aborts the whole backfill loop with a 500,
 * stopping task generation for every one of that user's templates, not just this one. Those
 * cadences stay with the template modal, which pairs the rate with the date picker that supplies
 * them; here they are shown as text.
 */
export const HoldTimeDialog = ({
    template,
    goal,
    open,
    onOpenChange,
}: {
    template: TRecurringTaskTemplate
    goal: Pick<TGoal, 'weeklyTarget' | 'unit' | 'status'>
    open: boolean
    onOpenChange: (open: boolean) => void
}) => {
    const { mutate: modifyRecurringTask } = useModifyRecurringTask()
    const [rate, setRate] = useState<RecurrenceRate>(template.recurrence_rate)
    const [startTime, setStartTime] = useState(secondsToTimeInput(template.time_of_day_seconds_to_create_task))
    const [minutes, setMinutes] = useState(String(template.block_duration_minutes || DEFAULT_BLOCK_MINUTES))

    const canEditRate = hasWeekRead(template)
    // The weekly option labels itself with a weekday, so it has to be this template's stored one:
    // anchoring it to today would read "Weekly on Wednesday" beside a ribbon marking Thursday, and
    // saving keeps Thursday. With no stored weekday, today is also what save() would write.
    const weeklyAnchor = isWeekday(template.day_to_create_task)
        ? DateTime.local().set({ weekday: template.day_to_create_task })
        : DateTime.local()
    const rateOptions = getRecurrenceRateSelectorOptions(weeklyAnchor).filter((option) =>
        WEEK_READ_RATES.includes(Number(option.value))
    )
    const parsedMinutes = Number(minutes)
    const isValid = Number.isFinite(parsedMinutes) && parsedMinutes > 0 && parsedMinutes <= 24 * 60
    const preview: TRecurringTaskTemplate = {
        ...template,
        recurrence_rate: rate,
        block_duration_minutes: isValid ? parsedMinutes : 0,
    }
    const heldHours = heldMinutesPerWeek(preview) / 60
    const target = goal.weeklyTarget ?? 0
    const coverage =
        goal.unit === 'hrs' && target > 0
            ? heldHours >= target
                ? `Holds ${formatGoalAmount(heldHours)} hrs a week — the ${formatGoalAmount(
                      target
                  )} hrs target is covered.`
                : `Holds ${formatGoalAmount(heldHours)} of ${formatGoalAmount(target)} hrs a week.`
            : `Holds ${formatMinutes(heldMinutesPerWeek(preview))} a week.`

    const save = () => {
        if (!isValid) return
        modifyRecurringTask(
            {
                id: template.id,
                // Unchanged for a monthly or yearly template: only the rates with a week read are
                // offered here, so a save can never drop a day or month the backend requires.
                recurrence_rate: rate,
                time_of_day_seconds_to_create_task: timeInputToSeconds(startTime),
                block_duration_minutes: parsedMinutes,
                // A weekly template needs a weekday, and a daily one carries no day at all — so
                // switching to weekly has to leave a valid one behind, or the backend stops
                // generating tasks from this template.
                ...(rate === RecurrenceRate.WEEKLY && !isWeekday(template.day_to_create_task)
                    ? { day_to_create_task: DateTime.local().weekday }
                    : {}),
            },
            template.optimisticId
        )
        onOpenChange(false)
    }

    const release = () => {
        modifyRecurringTask({ id: template.id, block_duration_minutes: 0 }, template.optimisticId)
        onOpenChange(false)
    }

    return (
        <GTDialog open={open} onOpenChange={onOpenChange} label="Hold time" onSubmit={save}>
            <GTDialogBody className="gap-4">
                <GTDialogHeading title={template.title} subtitle="Days come from how often it repeats." />
                <div className="space-y-1.5">
                    <span className="text-label-md text-muted-foreground">Repeats</span>
                    {canEditRate ? (
                        <GTSelect
                            items={rateOptions}
                            value={rate.toString()}
                            onChange={(next) => setRate(parseInt(next))}
                            useTriggerWidth
                        />
                    ) : (
                        <p className="text-body-md text-foreground">
                            {formatRecurrenceRateForScheduleButton(template)}
                        </p>
                    )}
                </div>
                <div className="flex flex-wrap items-end gap-4">
                    <label className="space-y-1.5">
                        <span className="block text-label-md text-muted-foreground">Starts</span>
                        <Input
                            type="time"
                            value={startTime}
                            onChange={(e) => setStartTime(e.target.value)}
                            onKeyDown={(e) => stopKeydownPropogation(e, ['Escape'])}
                            className="h-8 w-28"
                        />
                    </label>
                    <div className="space-y-1.5">
                        <span className="block text-label-md text-muted-foreground">Length</span>
                        <div className="flex items-center gap-1.5">
                            <Input
                                type="number"
                                min={5}
                                step={5}
                                value={minutes}
                                onChange={(e) => setMinutes(e.target.value)}
                                onKeyDown={(e) => stopKeydownPropogation(e, ['Escape'])}
                                className="h-8 w-20"
                                aria-label="Block length in minutes"
                            />
                            {LENGTH_SHORTCUT_MINUTES.map((shortcut) => (
                                <button
                                    key={shortcut}
                                    type="button"
                                    onClick={() => setMinutes(String(shortcut))}
                                    className={cn(
                                        'h-8 rounded-md border border-border px-2 text-label-md text-secondary-foreground transition-colors hover:bg-muted',
                                        parsedMinutes === shortcut && 'border-foreground bg-secondary text-foreground'
                                    )}
                                >
                                    {formatMinutes(shortcut)}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="space-y-1 rounded-gt-md border border-border bg-card p-3">
                    <WeekRibbon template={preview} showDayLetters />
                    <p className="text-body-sm text-muted-foreground">{coverage}</p>
                </div>
                <p className="text-body-sm text-muted-foreground">
                    Each new occurrence carries this length and counts it when you finish it.
                </p>
            </GTDialogBody>
            <GTDialogFooter
                start={
                    isHeld(template) ? (
                        <Button variant="ghost" size="sm" onClick={release}>
                            Release
                        </Button>
                    ) : undefined
                }
                onCancel={() => onOpenChange(false)}
                onConfirm={save}
                confirmLabel="Hold time"
                confirmDisabled={!isValid}
            />
        </GTDialog>
    )
}

interface RepeatsProps {
    goal: TGoal
    templates: TRecurringTaskTemplate[]
    candidates: TRecurringTaskTemplate[]
    onAdd: (template: TRecurringTaskTemplate) => void
    onRemove: (template: TRecurringTaskTemplate) => void
    onEditTarget: (patch: TGoalPatch) => void
}

const Repeats = ({ goal, templates, candidates, onAdd, onRemove, onEditTarget }: RepeatsProps) => {
    const [holdTemplateId, setHoldTemplateId] = useState<string | null>(null)
    const holdTemplate = templates.find((template) => template.id === holdTemplateId)
    const summary = repeatsSummary({ templates, goal })

    return (
        <>
            <SectionLabel>Repeats</SectionLabel>
            {templates.length === 0 ? (
                <p className="text-body-sm text-muted-foreground">
                    Nothing repeats yet — hook the recurring work that should move this goal every week.
                </p>
            ) : (
                <div className="flex flex-col">
                    {templates.map((template, index) => (
                        <RepeatRow
                            key={template.id}
                            template={template}
                            showDayLetters={index === 0}
                            onRemove={() => onRemove(template)}
                            onHoldTime={() => setHoldTemplateId(template.id)}
                        />
                    ))}
                </div>
            )}
            {summary.line && (
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <p className="text-body-sm text-muted-foreground">
                        {summary.line}
                        {/* Naming the repeat that holds nothing is the difference between a number
                            and an action: it says which row to open. */}
                        {goal.unit === 'hrs' && summary.unheldTitles.length > 0 && (
                            <> {summary.unheldTitles.slice(0, 2).join(' and ')} holds none.</>
                        )}
                    </p>
                    {summary.supportedTarget !== undefined && (
                        <button
                            type="button"
                            onClick={() => onEditTarget({ weeklyTarget: summary.supportedTarget, forgive: true })}
                            className="h-6 rounded-md px-1.5 text-label-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        >
                            Lower to {summary.supportedTarget}×
                        </button>
                    )}
                </div>
            )}
            <div className="flex items-center gap-1">
                <RecurringHookPopover candidates={candidates} onPick={onAdd} />
            </div>
            {holdTemplate && (
                <HoldTimeDialog
                    key={holdTemplate.id}
                    template={holdTemplate}
                    goal={goal}
                    open
                    onOpenChange={(next) => !next && setHoldTemplateId(null)}
                />
            )}
        </>
    )
}

export default Repeats
